import React from "react";

// Media is the hero. Renders generated hero key-art when available, otherwise a
// clean deterministic typographic development poster. The typographic fallback
// is a MONOGRAM derived deterministically from the canonical title (Dark -> D,
// Hollow Knight -> HK, Outer Wilds -> OW, Dark Souls -> DS) rendered as scalable
// SVG text so it fits any container size and never clips into nonsensical
// partial letters. The full title still renders in the meta label.

function hashAngle(id) {
  let h = 0;
  const s = id || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

const STOP = new Set([
  "the", "a", "an", "of", "and", "or", "to", "in", "on", "at", "&",
  "part", "vol", "chapter", "episode", "for",
]);

// Deterministic 1-2 letter initials from a canonical title.
export function deriveInitials(title) {
  if (!title) return "?";
  const words = String(title).replace(/[:_\-/]/g, " ").split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const significant = words.filter((w) => !STOP.has(w.toLowerCase()));
  const use = (significant.length ? significant : words).slice(0, 2);
  const letters = use
    .map((w) => {
      const m = w.match(/[A-Za-z0-9]/);
      return m ? m[0].toUpperCase() : "";
    })
    .join("");
  if (letters) return letters.slice(0, 2);
  const f = words[0].match(/[A-Za-z0-9]/);
  return f ? f[0].toUpperCase() : "?";
}

const DOMAIN_LABEL = { FILM: "FILM", SERIES: "SERIES", GAME: "GAME" };

export default function PosterArt({ work, className = "", showMeta = true, variant = "foreground" }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  if (!work) return <div aria-hidden className={`absolute inset-0 bg-[#0b0c11] ${className}`} />;
  const angle = hashAngle(work.id);
  const [c1, c2] = work.palette || ["#12141b", "#070709"];
  const initials = deriveInitials(work.title);
  // Prefer provider-backed real poster/cover; then legacy hero art; else fallback.
  const posterUrl = (work.poster && work.poster.url) || work.artwork || null;
  const showImg = posterUrl && !imgFailed;

  // BACKGROUND variant — the same resolved artwork enlarged, blurred and darkened
  // so the crisp hero poster reads as lifted off a cinematic extension of its own
  // world. Reuses the SAME src (one network fetch, cached) — never a duplicate
  // provider request. On fallback it extends the palette field instead of a grey
  // filler band. Purely atmospheric: it must never compete with the hero.
  if (variant === "background") {
    return (
      <div aria-hidden className={`absolute inset-0 overflow-hidden bg-[#070709] ${className}`}>
        {showImg ? (
          <img
            src={posterUrl}
            alt=""
            aria-hidden
            loading="eager"
            draggable={false}
            onError={() => setImgFailed(true)}
            className="absolute inset-0 w-full h-full object-cover select-none"
            style={{
              transform: "scale(1.28)",
              filter: "blur(30px) brightness(0.42) saturate(0.92)",
              objectPosition: "center 35%",
            }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(${angle}deg, ${c1} 0%, ${c2} 82%)`,
              filter: "brightness(0.5) saturate(0.95)",
              transform: "scale(1.12)",
            }}
          />
        )}
        {/* darken + vignette so the crisp hero always dominates the wide stage */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(7,7,9,0.12) 0%, rgba(7,7,9,0.68) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-black/25" />
      </div>
    );
  }

  return (
    <div className={`relative w-full h-full overflow-hidden bg-[#070709] ${className}`}>
      {showImg ? (
        /* Poster-first: a real 2:3 media viewport is filled edge-to-edge with the
           poster/cover. Focal bias is slightly above centre so faces/titles are
           preserved and there is NO dead/blurred filler band. */
        <img
          src={posterUrl}
          alt={work.title}
          loading="eager"
          draggable={false}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover select-none"
          style={{ objectPosition: "center 32%" }}
        />
      ) : (
        <div
          className="absolute inset-0 tt-grain"
          style={{ background: `linear-gradient(${angle}deg, ${c1} 0%, ${c2} 78%)` }}
        >
          <div
            className="absolute -right-8 -top-8 w-40 h-40 rounded-full blur-3xl opacity-40"
            style={{ background: c1 }}
          />
          {/* Deterministic monogram — scales to the container, never clips */}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 w-full h-full"
            aria-hidden="true"
          >
            <text
              x="50"
              y="52"
              textAnchor="middle"
              dominantBaseline="central"
              fontFamily="Chivo, Outfit, sans-serif"
              fontWeight="900"
              fontSize={initials.length > 1 ? 46 : 62}
              letterSpacing={initials.length > 1 ? "-3" : "0"}
              fill="rgba(255,255,255,0.16)"
            >
              {initials}
            </text>
          </svg>
        </div>
      )}

      {showMeta && (
        <>
          {/* Bottom scrim so overlaid title typography always reads */}
          <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
            <h3 className="font-display font-bold tracking-tight text-white text-sm sm:text-base leading-tight drop-shadow break-words [overflow-wrap:anywhere] line-clamp-2">
              {work.title}
            </h3>
            <p className="mt-0.5 font-body font-medium text-[11px] tracking-wide text-white/60">
              {DOMAIN_LABEL[work.domain] || work.domain} · {work.year}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
